// =============================================================
// 設問×ステップごとの所要時間（2026-10-12）
//
// 分析で使う時間は2つ。どちらも回答（step_responses）に入る：
//   duration_ms … そのステップを最初に開いてから、回答を送るまで
//                 （途中で別の問を見ていた時間・リロードしていた時間も含む）
//   active_ms   … そのうち、そのステップの画面が実際に出ていた時間の合計
//                 （タブで別の問を見ていた間・ブラウザのタブを隠していた／閉じていた間は数えない）
// 旧方式は「最後にその画面を開いた時刻から」だったため、回答前に別の問を見て戻ったり
// リロードしたりすると短く記録されていた。
//
// 最初に開いた時刻と表示時間の合計は、進み具合と同じ持ち主（番号／番号なしのタブ）ごとに
// 保存するので、リロードしても続きから数える。ヒントを押した回数（hints）も同じ記録に持つ。
// =============================================================

import { progressStorage } from './session';

const PREFIX = 'ex2026Timing';

// いま画面に出ているステップ。since＝表示中の区間の始まり（隠れている間は null）
let current = null;

const storageKey = (owner, exercise) => `${PREFIX}:${owner}:${exercise}`;

function load(owner, exercise) {
  try {
    const v = JSON.parse(progressStorage(owner).getItem(storageKey(owner, exercise)) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch (_) {
    return {};
  }
}

function save(owner, exercise, data) {
  try {
    progressStorage(owner).setItem(storageKey(owner, exercise), JSON.stringify(data));
  } catch (_) {
    // 保存できなければ、その場で数えた値だけで続ける
  }
}

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

// 表示中の区間を締めて合計に足す
function closeSegment(now = Date.now()) {
  if (!current || current.since == null) return;
  const data = load(current.owner, current.exercise);
  const entry = data[current.index] || { firstShownAt: current.since, activeMs: 0, hints: 0 };
  entry.activeMs += Math.max(0, now - current.since);
  data[current.index] = entry;
  save(current.owner, current.exercise, data);
  current.since = null;
}

/** そのステップを開いた（演習ルートが問×ステップを開くたびに呼ぶ）。前のステップの表示時間はここで締める。 */
export function beginStep(owner, exercise, index) {
  closeSegment();
  current = null;
  if (owner == null) return;
  const now = Date.now();
  const data = load(owner, exercise);
  if (!data[index]) {
    data[index] = { firstShownAt: now, activeMs: 0, hints: 0 };
    save(owner, exercise, data);
  }
  current = { owner, exercise, index, since: isHidden() ? null : now };
}

/** 演習の画面を離れた（完了画面・Home へ）。 */
export function endStep() {
  closeSegment();
  current = null;
}

/** いま開いているステップの時間（回答を送る・ヒントを押すときに使う）。開いていなければ null。 */
export function currentStepTiming(now = Date.now()) {
  if (!current) return null;
  const entry = load(current.owner, current.exercise)[current.index];
  if (!entry) return null;
  const running = current.since != null ? Math.max(0, now - current.since) : 0;
  return {
    firstShownAt: entry.firstShownAt,
    durationMs: Math.max(0, now - entry.firstShownAt),
    activeMs: entry.activeMs + running,
    hints: entry.hints || 0,
  };
}

/** いま開いているステップでヒントを押した回数を1つ増やし、増やした後の回数を返す（開いていなければ null）。 */
export function countHint() {
  if (!current) return null;
  const data = load(current.owner, current.exercise);
  const entry = data[current.index];
  if (!entry) return null;
  entry.hints = (entry.hints || 0) + 1;
  save(current.owner, current.exercise, data);
  return entry.hints;
}

/** その演習の時間の記録を消す（ログアウトのとき）。数えている途中の区間も捨てる。 */
export function clearTiming(owner, exercise) {
  if (owner == null) return;
  try {
    progressStorage(owner).removeItem(storageKey(owner, exercise));
  } catch (_) {
    // 何もできない
  }
  if (current && current.owner === owner && current.exercise === exercise) current = null;
}

// ブラウザのタブを隠したら表示時間を止め、戻ったら再開する。閉じる前にも締めて保存する
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!current) return;
    if (isHidden()) closeSegment();
    else if (current.since == null) current.since = Date.now();
  });
  window.addEventListener('pagehide', () => closeSegment());
}
